import { Link } from 'react-router-dom';
import { formatDiscount, expiryLabel, daysUntilExpiry, truncate } from '../../utils/helpers';
import MerchantLogo from '../common/MerchantLogo';
import VoteButton from './VoteButton';

export default function CouponCard({ coupon, onRedeem, compact, badge }) {
  const discount   = formatDiscount(coupon);
  const expiry     = expiryLabel(coupon.expires_at);
  const days       = daysUntilExpiry(coupon.expires_at);
  const isExpiring = days !== null && days <= 7 && days >= 0;
  const usageRatio = coupon.max_uses && coupon.used_count != null ? coupon.used_count / coupon.max_uses : null;

  return (
    <div className="coupon-card" style={{
      background: '#fff', border: '1px solid #E4E7F0', borderRadius: 16,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
      cursor: 'pointer', position: 'relative',
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(15,18,37,0.10)'; e.currentTarget.style.borderColor = '#D8C8F8'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#E4E7F0'; }}
    >
      {/* Top gradient stripe */}
      <div style={{ height: 3, background: 'var(--brand-gradient)' }} />

      <div style={{ padding: '18px 18px 0' }}>
        {/* Merchant, with the badges beside it. They used to float
            (position: absolute, top/right) over this row, so on a narrow card
            they sat on top of the merchant name and category — "SHOPPERS LIKE
            YOU" over "Booking.com" (QA 2026-09-21/22). In the flow they wrap
            under the name instead when there is no room. */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: '1 1 150px' }}>
          <MerchantLogo merchant={coupon.merchant} size={40} radius={10} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#0F1225', letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{coupon.merchant?.name || 'Merchant'}</p>
            {coupon.category && <p style={{ fontSize: 11, color: '#8B94A8', fontWeight: 500 }}>{coupon.category.name}</p>}
          </div>
          </div>

          {(badge || coupon.is_featured || coupon.is_exclusive || isExpiring) && (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'flex-start', flex: '0 1 auto', maxWidth: '100%' }}>
              {badge               && <span className="badge" style={{ background: '#EBE0FF', color: '#6B2FD9' }}>{badge}</span>}
              {coupon.is_featured  && <span className="badge badge-gradient">⭐ Featured</span>}
              {coupon.is_exclusive && <span className="badge" style={{ background: '#FEF3C7', color: '#B7791F' }}>🔒 Exclusive</span>}
              {isExpiring          && <span className="badge" style={{ background: '#FEE2E2', color: '#DC2626' }}>🔥 Ends in {days}d</span>}
            </div>
          )}
        </div>

        {/* Discount headline */}
        <div style={{ background: 'linear-gradient(135deg, #F3EEFF 0%, #FFE8D6 100%)', borderRadius: 12, padding: '14px 16px', marginBottom: 14, textAlign: 'center' }}>
          <p className="gradient-text" style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>{discount}</p>
          {coupon.min_order_amount && <p style={{ fontSize: 11, color: '#4A5568', marginTop: 2 }}>on orders over ${coupon.min_order_amount}</p>}
        </div>

        {/* Title */}
        <h3 style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.45, marginBottom: 8, letterSpacing: '-0.01em' }}>
          <Link to={`/coupons/${coupon.id}`} style={{ color: '#0F1225', textDecoration: 'none' }}>
            {coupon.title}
          </Link>
        </h3>

        {!compact && coupon.description && (
          <p style={{ fontSize: 13, color: '#4A5568', lineHeight: 1.55, marginBottom: 12 }}>
            {truncate(coupon.description, 90)}
          </p>
        )}

        {/* Tags */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {coupon.is_online   && <span style={tagStyle}>🌐 Online</span>}
          {coupon.is_in_store && <span style={tagStyle}>📍 In-store</span>}
          {coupon.code_type === 'unique' && <span style={{ ...tagStyle, background: '#F3EEFF', color: '#6B2FD9' }}>🎫 Personal code</span>}
        </div>

        {/* Usage bar */}
        {usageRatio !== null && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8B94A8', fontWeight: 600, marginBottom: 4 }}>
              <span>Claimed</span>
              <span>{coupon.used_count}/{coupon.max_uses}</span>
            </div>
            <div style={{ height: 4, background: '#F0F2F8', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${Math.min(usageRatio * 100, 100)}%`, background: usageRatio > 0.8 ? 'var(--danger)' : 'var(--brand-gradient)', transition: 'width 0.3s' }} />
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid #F0F2F8', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', gap: 10 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={{ fontSize: 11, color: days !== null && days <= 3 ? '#DC2626' : '#8B94A8', fontWeight: 600 }}>
            {expiry}
          </p>
          {coupon.verified_at && coupon.verification_status !== 'broken' && (
            <p style={{ fontSize: 10, color: '#0F9D6E', fontWeight: 600, marginTop: 2 }}>✓ {freshnessLabel(coupon.verified_at)}</p>
          )}
          {coupon.verification_status === 'broken' && (
            <p style={{ fontSize: 10, color: '#DC2626', fontWeight: 700, marginTop: 2 }}>⚠ Reported broken</p>
          )}
        </div>
        <VoteButton coupon={coupon} size="sm" />
        <button
          onClick={(e) => { e.stopPropagation(); onRedeem && onRedeem(coupon); }}
          className="btn btn-primary btn-sm"
          style={{ fontSize: 12, padding: '8px 16px', borderRadius: 999, flexShrink: 0 }}
        >
          Get Deal →
        </button>
      </div>

      {coupon.ftc_disclosure && (
        <div style={{ padding: '0 18px 10px' }}>
          <p className="ftc-notice" style={{ fontSize: 10 }}>*Affiliate — we may earn a commission.</p>
        </div>
      )}
    </div>
  );
}

function freshnessLabel(verifiedAt) {
  const hours = Math.floor((Date.now() - new Date(verifiedAt).getTime()) / 3600000);
  if (hours < 1) return 'Verified just now';
  if (hours < 24) return `Verified ${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Verified yesterday';
  return `Verified ${days}d ago`;
}

const tagStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999,
  background: '#F7F8FC', color: '#4A5568',
};
